import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { casesApi, teamApi } from '../../../services/api';
import { ProjectListItem } from '../../../types';
import { X } from 'lucide-react';

interface CaseFormProps {
  onClose: () => void;
  onSuccess: () => void;
  projects: ProjectListItem[];
  initialProjectId?: number;
}

const CaseForm: React.FC<CaseFormProps> = ({ onClose, onSuccess, projects, initialProjectId }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<any[]>([]);

  // Calculate default due date (7 days from today)
  const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const [form, setForm] = useState({
    case_subject: '',
    project_id: (initialProjectId ?? null) as number | null,
    case_priority: 'Normal' as string,
    case_status: 'Ready' as string,
    assigned_user_id: null as number | null,
    type: 'Prior' as string,
    due_date: getDefaultDueDate(),
    description: '',
  });

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const data = await teamApi.list({ per_page: 1000 });
        setTeams(data.data || []);
      } catch (error) {
        console.error('Failed to load teams:', error);
      }
    };
    loadTeams();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'assigned_team_id' || name === 'project_id' ? (value ? Number(value) : null) : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.case_subject.trim()) {
      setError('Case Subject is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload: any = {
        case_subject: form.case_subject,
        case_priority: form.case_priority,
        case_status: form.case_status,
        type: form.type,
        description: form.description || null,
      };

      if (form.project_id) payload.project_id = form.project_id;
      if (form.assigned_team_id) payload.assigned_team_id = form.assigned_team_id;
      if (form.due_date) payload.due_date = form.due_date;

      await casesApi.create(payload);
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create case');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={onClose}>
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">Create Case</h2>
          <button
            onClick={onClose}
            className="p-1 hover:opacity-80 rounded-full transition-opacity"
          >
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Case Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="case_subject"
              value={form.case_subject}
              onChange={handleChange}
              required
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter case subject"
            />
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Project</label>
            <select
              name="project_id"
              value={form.project_id || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, project_id: e.target.value ? Number(e.target.value) : null }))}
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a project (optional)</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Type</label>
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Prior">Prior</option>
                <option value="Rights">Rights</option>
                <option value="Research">Research</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Priority</label>
              <select
                name="case_priority"
                value={form.case_priority}
                onChange={handleChange}
                className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Normal">Normal</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
                <option value="Immediate">Immediate</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Status</label>
              <select
                name="case_status"
                value={form.case_status}
                onChange={handleChange}
                className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ready">Ready</option>
                <option value="working">Working</option>
                <option value="waiting">Waiting</option>
                <option value="reviewing">Reviewing</option>
                <option value="approved">Approved</option>
                <option value="uploading">Uploading</option>
                <option value="closed">Closed</option>
                <option value="additional_request">Additional Request</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Due Date</label>
              <input
                type="date"
                name="due_date"
                value={form.due_date}
                onChange={handleChange}
                className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Assigned Team</label>
            <select
              name="assigned_team_id"
              value={form.assigned_team_id}
              onChange={handleChange}
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter case description (optional)"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80 text-sm"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !form.case_subject.trim()}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50 text-sm"
            >
              {submitting ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CaseForm;

